import { isAxiosError } from "axios";

type UnknownRecord = Record<string, unknown>;
const DEFAULT_AXIOS_404_MESSAGE = "Request failed with status code 404";
const TECHNICAL_NOT_FOUND_MESSAGE_PATTERNS = [
  "API endpoint not found:",
  "Server returned HTML instead of JSON",
  "Received HTML instead of JSON response",
];

const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === "object" && value !== null;
};

const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const getNestedRecord = (record: UnknownRecord, key: string): UnknownRecord | undefined => {
  const value = record[key];
  return isRecord(value) ? value : undefined;
};

const getFirstErrorMessage = (value: unknown): string | undefined => {
  if (!Array.isArray(value)) return undefined;

  for (const item of value) {
    if (isRecord(item)) {
      const msg = toNonEmptyString(item.message);
      if (msg) return msg;
      continue;
    }

    const msg = toNonEmptyString(item);
    if (msg) return msg;
  }

  return undefined;
};

const getStatusCodeFromPayload = (payload: unknown): number | undefined => {
  if (!isRecord(payload)) return undefined;

  const nestedData = getNestedRecord(payload, "data");
  return toNumber(payload.statusCode) ?? toNumber(nestedData?.statusCode);
};

const extractMessageFromPayload = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) return undefined;

  const nestedData = getNestedRecord(payload, "data");

  return (
    toNonEmptyString(nestedData?.developerMessage) ||
    toNonEmptyString(payload.developerMessage) ||
    toNonEmptyString(nestedData?.message) ||
    toNonEmptyString(payload.message) ||
    toNonEmptyString(nestedData?.error) ||
    toNonEmptyString(payload.error) ||
    getFirstErrorMessage(nestedData?.errors) ||
    getFirstErrorMessage(payload.errors)
  );
};

export const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (isAxiosError(error)) {
    return extractMessageFromPayload(error.response?.data) || toNonEmptyString(error.message);
  }

  if (error instanceof Error) {
    return toNonEmptyString(error.message);
  }

  return toNonEmptyString(error);
};

const getStatusCodeFromError = (error: unknown): number | undefined => {
  if (isAxiosError(error)) {
    return error.response?.status ?? getStatusCodeFromPayload(error.response?.data);
  }

  if (!isRecord(error)) {
    return undefined;
  }

  return toNumber(error.statusCode);
};

export const isNotFoundError = (error: unknown): boolean => {
  if (isRecord(error) && error.isApiNotFound === true) {
    return true;
  }

  return getStatusCodeFromError(error) === 404;
};

export const getNotFoundErrorMessage = (
  error: unknown,
  fallback = "Requested resource was not found.",
): string => {
  const errorMessage = extractApiErrorMessage(error);

  if (
    !errorMessage ||
    errorMessage === DEFAULT_AXIOS_404_MESSAGE ||
    TECHNICAL_NOT_FOUND_MESSAGE_PATTERNS.some((pattern) => errorMessage.includes(pattern))
  ) {
    return fallback;
  }

  return errorMessage;
};

export const isConflictError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;

  return error.response?.status === 409 || getStatusCodeFromPayload(error.response?.data) === 409;
};

export const getConflictErrorMessage = (
  error: unknown,
  fallback = "Request conflict detected. Please refresh and try again.",
): string => {
  return extractApiErrorMessage(error) || fallback;
};
